import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type Id = string | number;

type MessagesEntry = {
  messages: any[];
  isLoading: boolean;
  error: any;
  fetchedAt: number | null;
  meta?: any;
};

const messageKey = (m: any) =>
  String(m?.message_id ?? m?.id ?? m?.client_id ?? "");

type CachedUser = {
  user_id: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  [key: string]: any;
};

type SupportState = {
  messagesByChatId: Record<string, MessagesEntry | undefined>;
  usersById: Record<string, CachedUser | undefined>;
};

const initialState: SupportState = {
  messagesByChatId: {},
  usersById: {},
};

const toKey = (chatId: Id) => String(chatId);

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    clearSupportState() {
      return initialState;
    },

    fetchMessagesRequest(state, action: PayloadAction<{ chatId: Id; silent?: boolean }>) {
      const key = toKey(action.payload.chatId);
      const prev: MessagesEntry =
        state.messagesByChatId[key] ??
        ({ messages: [], isLoading: false, error: null, fetchedAt: null, meta: null } as MessagesEntry);
      state.messagesByChatId[key] = {
        ...prev,
        isLoading: action.payload.silent ? prev.isLoading : true,
        error: null,
      };
    },

    fetchMessagesSuccess(
      state,
      action: PayloadAction<{
        chatId: Id;
        messages: any[];
        fetchedAt: number;
        mode?: "replace" | "prepend";
        meta?: any;
      }>
    ) {
      const key = toKey(action.payload.chatId);
      const prev: MessagesEntry =
        state.messagesByChatId[key] ??
        ({ messages: [], isLoading: false, error: null, fetchedAt: null, meta: null } as MessagesEntry);

      const incoming = Array.isArray(action.payload.messages)
        ? action.payload.messages
        : [];

      let nextMessages: any[] = incoming;
      if (action.payload.mode === "prepend") {
        const seen = new Set<string>();
        const merged = [...incoming, ...(prev.messages ?? [])];
        nextMessages = merged.filter((m) => {
          const k = messageKey(m);
          if (!k) return true;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      }

      state.messagesByChatId[key] = {
        messages: nextMessages,
        isLoading: false,
        error: null,
        fetchedAt: action.payload.fetchedAt,
        meta: action.payload.meta ?? prev.meta ?? null,
      };
    },

    fetchMessagesFailed(state, action: PayloadAction<{ chatId: Id; error: any }>) {
      const key = toKey(action.payload.chatId);
      const prev: MessagesEntry =
        state.messagesByChatId[key] ??
        ({ messages: [], isLoading: false, error: null, fetchedAt: null, meta: null } as MessagesEntry);
      state.messagesByChatId[key] = {
        ...prev,
        isLoading: false,
        error: action.payload.error,
      };
    },

    cacheUser(state, action: PayloadAction<CachedUser>) {
      const key = String(action.payload.user_id);
      state.usersById[key] = action.payload;
    },
  },
});

export const {
  clearSupportState,
  fetchMessagesRequest,
  fetchMessagesSuccess,
  fetchMessagesFailed,
  cacheUser,
} = supportSlice.actions;

export default supportSlice.reducer;
